import React, { useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaSearch,
  FaTh,
  FaList,
  FaTable,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const PAGE_SIZE = 5;

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [view, setView] = useState("table"); // table | grid | list
  const [page, setPage] = useState(1);

  // 🔔 Mock data
  useEffect(() => {
    setNotifications([
      {
        id: 1,
        type: "DRIVER",
        title: "Driver Went Offline",
        message: "Driver John Doe went offline.",
        priority: "HIGH",
        isRead: false,
        createdAt: "2026-01-27",
      },
      {
        id: 2,
        type: "RIDE",
        title: "Ride Completed",
        message: "Ride TRP-90231 completed.",
        priority: "LOW",
        isRead: true,
        createdAt: "2026-01-26",
      },
      {
        id: 3,
        type: "SYSTEM",
        title: "High Server Load",
        message: "CPU load exceeded 85%",
        priority: "CRITICAL",
        isRead: false,
        createdAt: "2026-01-25",
      },
      {
        id: 4,
        type: "DRIVER",
        title: "New Driver Registered",
        message: "Driver Ahmed Ali registered.",
        priority: "LOW",
        isRead: true,
        createdAt: "2026-01-24",
      },
    ]);
  }, []);

  // 🔎 Filter logic
  const filtered = useMemo(() => {
    return notifications.filter((n) => {
      const matchesSearch =
        n.title.toLowerCase().includes(search.toLowerCase()) ||
        n.message.toLowerCase().includes(search.toLowerCase()) ||
        n.type.toLowerCase().includes(search.toLowerCase());

      const matchesDate = date ? n.createdAt === date : true;

      return matchesSearch && matchesDate;
    });
  }, [notifications, search, date]);

  // 📄 Pagination
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const badgeByPriority = (p) => {
    if (p === "CRITICAL") return "bg-danger";
    if (p === "HIGH") return "bg-warning text-dark";
    return "bg-secondary";
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-2xl font-bold">Notifications</h2>

          <div className="btn-group">
            <button
              className={`btn btn-outline-primary ${view === "table" && "active"}`}
              onClick={() => setView("table")}
            >
              <FaTable />
            </button>
            <button
              className={`btn btn-outline-primary ${view === "grid" && "active"}`}
              onClick={() => setView("grid")}
            >
              <FaTh />
            </button>
            <button
              className={`btn btn-outline-primary ${view === "list" && "active"}`}
              onClick={() => setView("list")}
            >
              <FaList />
            </button>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="card shadow-sm mb-4">
          <div className="card-body d-flex gap-3 flex-wrap">
            <div className="input-group w-50">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <input
                className="form-control"
                placeholder="Search notifications..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <input
              type="date"
              className="form-control w-auto"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        {/* TABLE VIEW */}
        {view === "table" && (
          <div className="card shadow-sm">
            <table className="table table-bordered table-striped">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((n) => (
                  <tr key={n.id}>
                    <td>{n.title}</td>
                    <td>{n.type}</td>
                    <td>
                      <span className={`badge ${badgeByPriority(n.priority)}`}>
                        {n.priority}
                      </span>
                    </td>
                    <td>
                      {n.isRead ? (
                        <span className="text-success">Read</span>
                      ) : (
                        <span className="text-danger">Unread</span>
                      )}
                    </td>
                    <td>{n.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* GRID VIEW */}
        {view === "grid" && (
          <div className="row g-3">
            {paginated.map((n) => (
              <div className="col-md-4" key={n.id}>
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="fw-bold">{n.title}</h6>
                    <p className="text-muted">{n.message}</p>
                    <span className={`badge ${badgeByPriority(n.priority)}`}>
                      {n.priority}
                    </span>
                    <div className="mt-2 small text-muted">{n.createdAt}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* LIST VIEW */}
        {view === "list" && (
          <div className="list-group shadow-sm">
            {paginated.map((n) => (
              <div key={n.id} className="list-group-item">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6 className="fw-bold mb-1">{n.title}</h6>
                    <p className="mb-1 text-muted">{n.message}</p>
                  </div>
                  <span className={`badge ${badgeByPriority(n.priority)}`}>
                    {n.priority}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        <div className="d-flex justify-content-end mt-4 gap-2">
          <button
            className="btn btn-outline-secondary"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>
          <span className="align-self-center">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-outline-secondary"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notification;
